import React, { useState, useEffect, useContext } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@material-ui/core";
import { useMutation, useQuery } from "@apollo/client";
import { isValidPhoneNumber } from "react-phone-number-input";
import DeliveryNotes from "../../../shared/components/delivery-notes/delivery-notes.component.jsx";
import {
  h5Medium,
  h6Medium,
  h6Regular,
  pointer,
  h3,
} from "../../../shared/constants";
import { ReactComponent as DeliveryTruckIcon } from "../../../assets/deliveryTruck.svg";

import { CREATE_ADDRESS, GET_ACTIVE_USER } from "../../../queries/auth.queries";
import { SET_SHIPPING_ORDER } from "../../../queries/checkout.queries";
import { useTranslation } from "react-i18next";
import AddressForm from "../../account/add-address/address-form.component";
import { UPDATE_ADDRESS } from "../../../queries/user.queries";
import Loader from "../../common/loader/loader.component";
import { ErrorHandlingContext } from "../../../context/error-handling.context";
import { GET_ACTIVE_ORDER } from "../../../queries/cart.queries";
import { useShippingAddresses } from "./hooks/useShippingAddresses";
import SavedAddresses from "./SavedAddresses/SavedAddresses";

const useStyles = makeStyles({
  bodyAddresses: {
    overflow: "auto",
  },
  bodyTitles: {
    display: "flex",
    marginBottom: 32,

    "@media (max-width:940px)": {
      marginBottom: 21,
    },
  },

  button: {
    height: 56,
    width: 240,
    border: "none",
    textAlign: "center",
    borderRadius: 16,
    marginTop: 24,
    ...h5Medium,
    color: "#FFFFFF",
    ...pointer,
  },

  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

const AddressProcess = ({ setProcesses, processes, onEditAddress }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { addresses, selected, setSelected, loading } = useShippingAddresses();

  const savedShipping = t("checkout.Saved");
  const addNewShipping = t("checkout.Add New");

  const { setErrorHandlingState } = useContext(ErrorHandlingContext);

  const { data: userData } = useQuery(GET_ACTIVE_USER);
  const { data: orderData } = useQuery(GET_ACTIVE_ORDER);

  const { stage2UserDetails, stage3DeliverTo } = processes;

  const [closeAccordion, setCloseAccordion] = useState(false);
  const [isValidInput, setIsVaildInput] = useState(false);
  const [isValidContinue, setIsVaildContinue] = useState(false);
  const [currentNav, setCurrentNav] = useState(savedShipping);
  const [navState, setNavState] = useState({
    [savedShipping]: true,
    [addNewShipping]: false,
  });

  const [newAddress, setNewAddress] = useState({
    fullName: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    province: "",
    postalCode: "",
    countryCode: "IL",
    phoneNumber: "",
    defaultShippingAddress: false,
    defaultBillingAddress: false,
  });

  const currentOrderAddress = orderData?.activeOrder?.shippingAddress;
  const isFirstAddress = addresses.length === 0;

  // Accordion open/close based on previous stage
  useEffect(() => {
    setCloseAccordion(stage2UserDetails);
  }, [stage2UserDetails]);

  // Decide default tab based on whether user has saved addresses
  useEffect(() => {
    if (addresses.length > 0) {
      setCurrentNav(savedShipping);
      setNavState({
        [savedShipping]: true,
        [addNewShipping]: false,
      });
    } else {
      setCurrentNav(addNewShipping);
      setNavState({
        [savedShipping]: false,
        [addNewShipping]: true,
      });
    }
  }, [addresses.length, savedShipping, addNewShipping]);

  // Mutations

  const [setShippingAddress, { loading: setOrderShippingLoader }] = useMutation(
    SET_SHIPPING_ORDER,
    {
      refetchQueries: [{ query: GET_ACTIVE_USER }, { query: GET_ACTIVE_ORDER }],
      awaitRefetchQueries: true,
      onError: () => {
        setErrorHandlingState({
          showError: true,
          data: "An internal error has occurred",
        });
      },
    }
  );

  const [createAddress, { loading: createAddressLoading }] = useMutation(
    CREATE_ADDRESS,
    {
      refetchQueries: [{ query: GET_ACTIVE_USER }],
      awaitRefetchQueries: true,
      onError: () => {
        setErrorHandlingState({
          showError: true,
          data: "An internal error has occurred",
        });
      },
    }
  );

  const [updateAddress, { loading: updateAddressLoader }] = useMutation(
    UPDATE_ADDRESS,
    {
      refetchQueries: [{ query: GET_ACTIVE_USER }],
      awaitRefetchQueries: true,
      onError: () => {
        setErrorHandlingState({
          showError: true,
          data: "An internal error has occurred",
        });
      },
    }
  );

  // Helpers

  const buildShippingVariablesFromAddress = (address) => {
    if (!address) return {};

    const customer = userData?.activeCustomer;

    return {
      // These variable names assume your SET_SHIPPING_ORDER document
      // uses scalar variables, not a single `input` variable.
      fullName: address.fullName || customer?.firstName || "",
      company: address.company || "",
      streetLine1: address.streetLine1,
      streetLine2: address.streetLine2 || "",
      city: address.city || "",
      province: address.province || "",
      postalCode: address.postalCode || "",
      countryCode: address.countryCode || "IL",
      phoneNumber: address.phoneNumber || customer?.phoneNumber || "",
      defaultShippingAddress: address.defaultShippingAddress ?? false,
      defaultBillingAddress: address.defaultBillingAddress ?? false,
    };
  };

  const prepareMovingToNextStage = () => {
    setCurrentNav(savedShipping);
    setCloseAccordion(false);
    setProcesses();
    setNavState({
      [savedShipping]: true,
      [addNewShipping]: false,
    });
  };

  const setShippingForSelected = async () => {
    if (!selected) return;

    const variables = buildShippingVariablesFromAddress(selected);

    try {
      const { data } = await setShippingAddress({ variables });
      if (data?.setOrderShippingAddress?.message) {
        setErrorHandlingState({
          showError: true,
          data: data.setOrderShippingAddress.message,
        });
        return false;
      }
      return true;
    } catch {
      // onError handler already shows a generic error
      return false;
    }
  };

  const setShippingAndCreateForNew = async () => {
    const effectiveAddress = {
      ...newAddress,
      defaultShippingAddress:
        isFirstAddress || newAddress.defaultShippingAddress,
      defaultBillingAddress: isFirstAddress || newAddress.defaultBillingAddress,
    };

    const variables = buildShippingVariablesFromAddress(effectiveAddress);

    try {
      const { data } = await setShippingAddress({ variables });

      if (data?.setOrderShippingAddress?.message) {
        setErrorHandlingState({
          showError: true,
          data: data.setOrderShippingAddress.message,
        });
        return;
      }

      const addressResponse = await createAddress({ variables });

      if (
        addressResponse?.data?.createCustomerAddress?.errorCode &&
        addressResponse?.data?.createCustomerAddress?.message
      ) {
        setErrorHandlingState({
          showError: true,
          data: addressResponse.data.createCustomerAddress.message,
        });
        return;
      }

      prepareMovingToNextStage();
    } catch {
      // onError handlers show generic error
    }
  };

  // Continue button enabled state
  useEffect(() => {
    if (currentNav === savedShipping) {
      setIsVaildContinue(!!selected);
      return;
    }

    if (currentNav === addNewShipping) {
      const hasBasicFields =
        newAddress.streetLine1 &&
        newAddress.phoneNumber &&
        isValidInput &&
        isValidPhoneNumber(String(newAddress.phoneNumber || ""));
      setIsVaildContinue(!!hasBasicFields);
      return;
    }

    setIsVaildContinue(false);
  }, [
    currentNav,
    savedShipping,
    addNewShipping,
    selected,
    newAddress.streetLine1,
    newAddress.phoneNumber,
    isValidInput,
  ]);

  // Tab click handler
  const onNavClicked = (e) => {
    const { innerText } = e.target;
    setNavState({
      [savedShipping]: false,
      [addNewShipping]: false,
      [innerText]: true,
    });
    setCurrentNav(innerText);
  };

  // Main continue handler
  const onContinue = async () => {
    // Flow A — existing saved address
    if (currentNav === savedShipping && selected) {
      try {
        await updateAddress({
          variables: {
            id: selected.id,
            // per Q2 → B, we do NOT change defaults automatically;
            // we just send whatever the address already has.
            defaultShippingAddress: selected.defaultShippingAddress,
            defaultBillingAddress: selected.defaultBillingAddress,
          },
        });

        const ok = await setShippingForSelected();
        if (ok) {
          prepareMovingToNextStage();
        }
      } catch {
        // onError already handled
      }
      return;
    }

    // Flow B — new address
    if (currentNav === addNewShipping) {
      await setShippingAndCreateForNew();
    }
  };

  const renderBody = () => {
    if (currentNav === addNewShipping) {
      return (
        <div>
          {createAddressLoading ? (
            <Loader color={"#B73232"} size={50} />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                maxWidth: "660px",
              }}
            >
              <AddressForm
                addressDetails={newAddress}
                setAddressDetails={setNewAddress}
                setIsVaildInput={setIsVaildInput}
                isFirstAddress={isFirstAddress}
              />
            </div>
          )}
        </div>
      );
    }

    // savedShipping or default
    return (
      <SavedAddresses
        addresses={addresses}
        selectedAddress={selected}
        onSelect={setSelected}
      />
    );
  };

  return (
    <React.Fragment>
      <Accordion
        style={{
          borderRadius: "16px",
          padding: 30,
          boxShadow: "0px 16px 32px rgba(29, 38, 102, 0.08)",
          marginBottom: 24,
          borderBottom: "none",
          background: "#FFF",
        }}
        expanded={closeAccordion}
        disabled={!stage2UserDetails}
      >
        <AccordionSummary>
          <Typography style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <DeliveryTruckIcon
                  style={{
                    marginRight: t("css.side") !== "ltr" ? 0 : 16,
                    marginLeft: t("css.side") !== "ltr" ? 16 : 0,
                    width: 24,
                    height: 24,
                  }}
                />
                <span
                  style={{
                    ...h3,
                    fontFamily:
                      t("css.side") !== "ltr"
                        ? "Assistant"
                        : "DM Serif Display",
                  }}
                >
                  {t("account.deliveryTo")}
                </span>
              </div>
              {stage3DeliverTo ? (
                <span
                  style={{
                    ...h6Medium,
                    color: "#80BB34",
                  }}
                  onClick={() => {
                    setCloseAccordion(true);
                    onEditAddress();
                  }}
                >
                  {t("common.edit")}
                </span>
              ) : null}
            </div>
            {!closeAccordion && (
              <div
                style={{
                  ...h6Regular,
                  opacity: 0.8,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {currentOrderAddress?.streetLine1 && (
                  <>
                    <span style={{ ...h6Regular, opacity: 0.8 }}>
                      {currentOrderAddress?.streetLine1},{" "}
                      {currentOrderAddress?.city},
                      {currentOrderAddress?.postalCode ??
                        `${currentOrderAddress?.postalCode}, `}
                      {currentOrderAddress?.countryCode}
                    </span>
                    <span style={{ ...h6Regular, opacity: 0.8 }}>
                      {currentOrderAddress?.phoneNumber}
                    </span>
                  </>
                )}
              </div>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <div className={classes.bodyTitles}>
              {[savedShipping, addNewShipping].map((tabName, index) => (
                <span
                  key={index}
                  style={{
                    ...h5Medium,
                    marginRight: 32,
                    ...pointer,
                    opacity: navState[tabName] ? 1 : 0.64,
                  }}
                  onClick={onNavClicked}
                >
                  {tabName}
                </span>
              ))}
            </div>
            {loading ? <Loader color={"#B73232"} size={50} /> : renderBody()}
            <div style={{ width: "50%" }}>
              <DeliveryNotes onTextChange={(text) => console.log(text)} />
            </div>
            <button
              className={classes.button}
              style={{
                background: isValidContinue ? "#80BB34" : "lightgray",
              }}
              onClick={onContinue}
              disabled={!isValidContinue}
            >
              {setOrderShippingLoader ||
              updateAddressLoader ||
              createAddressLoading ? (
                <Loader color={"#B73232"} size={30} />
              ) : (
                t("common.Continue")
              )}
            </button>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </React.Fragment>
  );
};

export default AddressProcess;
